/**
 * Checkout delivery vs collection card selector.
 */

import { applyCheckoutDeliveryMethod } from './checkout-fields.js';

function getConfig() {
	return window.doneCommerce || {};
}

async function apiFetch(path, options = {}) {
	const config = getConfig();
	const response = await fetch(`${config.restUrl}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config.nonce,
			...(options.headers || {}),
		},
	});

	if (!response.ok) {
		throw new Error('Request failed');
	}

	return response.json();
}

function collectionDescription() {
	const cc = getConfig().clickCollect || {};
	const parts = [];

	if (cc.storeName) {
		parts.push(cc.storeName);
	}

	if (cc.preparationTime) {
		parts.push(`Ready in around ${cc.preparationTime}`);
	}

	return parts.join(' · ') || 'Pick up in store when your order is ready.';
}

function cardIcon(id) {
	if (id === 'collection') {
		// Flat storefront / pickup icon
		return `<svg class="dc-delivery-card__icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20h14V9.5"/><path d="M9 20v-6h6v6"/></svg>`;
	}

	// Flat delivery truck icon
	return `<svg class="dc-delivery-card__icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v10H3z"/><path d="M14 10h4l3 3v4h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>`;
}

function renderCards(container, options) {
	const cards = [
		{
			id: 'collection',
			title: 'FREE Click & Collect',
			desc: collectionDescription(),
			enabled: options.collection !== false,
		},
		{
			id: 'delivery',
			title: 'Delivery',
			desc: 'Australia wide · Calculated by order weight',
			enabled: options.delivery !== false,
		},
	];

	container.innerHTML = cards
		.filter((card) => card.enabled)
		.map(
			(card) => `
		<label class="dc-delivery-card" data-delivery-card="${card.id}">
			<input type="radio" name="dc_delivery_method" value="${card.id}">
			<span class="dc-delivery-card__icon-wrap">${cardIcon(card.id)}</span>
			<span class="dc-delivery-card__body">
				<span class="dc-delivery-card__title">${card.title}</span>
				<span class="dc-delivery-card__desc">${card.desc}</span>
			</span>
		</label>
	`
		)
		.join('');
}

function updateDeliveryNotice(container, cartMeta) {
	let notice = container.parentElement?.querySelector('[data-delivery-notice]');

	if (!notice) {
		notice = document.createElement('p');
		notice.className = 'dc-delivery-notice';
		notice.setAttribute('data-delivery-notice', '');
		container.parentElement?.insertBefore(notice, container.nextSibling);
	}

	const config = getConfig();
	let message = '';

	if (cartMeta?.has_collection_only) {
		message =
			config.i18n?.collectionOnlyNotice ||
			'One or more items in your order are available for Click & Collect only.';
	} else if (cartMeta?.unknown_weight) {
		message =
			config.i18n?.unknownWeightNotice ||
			'Delivery is not available until product weights are set.';
	} else if (cartMeta?.delivery_available === false) {
		message =
			config.i18n?.deliveryUnavailableNotice ||
			'Delivery is not available for your cart weight.';
	}

	if (message) {
		notice.textContent = message;
		notice.hidden = false;
	} else {
		notice.textContent = '';
		notice.hidden = true;
	}
}

function setActive(container, method) {
	container.querySelectorAll('.dc-delivery-card').forEach((card) => {
		const input = card.querySelector('input');
		const active = input?.value === method;
		card.classList.toggle('is-active', active);
		if (input) {
			input.checked = active;
		}
	});
}

function announceMethod(method) {
	applyCheckoutDeliveryMethod(method);
	document.dispatchEvent(
		new CustomEvent('dc:delivery-method', {
			detail: { method },
		})
	);

	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).trigger('update_checkout');
	}
}

export function initCheckoutDelivery() {
	const container = document.querySelector('[data-delivery-cards]');

	if (!container) {
		return;
	}

	const load = async () => {
		try {
			const availability = await apiFetch('done-commerce/v1/checkout/delivery-method');
			const options = availability.options || availability.available || availability;
			const cartMeta = availability.cart_meta || {};

			renderCards(container, options);
			updateDeliveryNotice(container, cartMeta);

			const current =
				availability.method ||
				(options.delivery === false && options.collection !== false ? 'collection' : '');
			setActive(container, current);
			if (current) {
				announceMethod(current);
			}

			container.addEventListener('change', async (e) => {
				const input = e.target.closest('input[name="dc_delivery_method"]');
				if (!input) {
					return;
				}

				setActive(container, input.value);
				announceMethod(input.value);

				try {
					await apiFetch('done-commerce/v1/checkout/delivery-method', {
						method: 'POST',
						body: JSON.stringify({ method: input.value }),
					});
					if (typeof window.jQuery !== 'undefined') {
						window.jQuery(document.body).trigger('update_checkout');
					}
				} catch {
					// Session update failed silently; WooCommerce validation catches at submit.
				}
			});
		} catch {
			renderCards(container, { delivery: true, collection: true });
			setActive(container, '');
		}
	};

	load();
}
